import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function SearchScreen() {
  const [query, setQuery] = useState("");
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [customIngredient, setCustomIngredient] = useState(""); // Stores user input for "Other"
  const [useCustomIngredient, setUseCustomIngredient] = useState(false); // Tracks if "Other" is checked
  const navigate = useNavigate();

  // Hardcoded options for ingredients
  const ingredients = ["Egg", "Milk", "Nuts", "Soy", "Artificial Colors"];

  // Handle checkbox change
  const handleCheckboxChange = (value) => {
    setSelectedIngredients((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const [certifications, setCertifications] = useState({
    CertifiedVegan: false,
    CertifiedPlantBased: false,
    CertifiedVegetarian: false,
    CertifiedKosher: false,
    CertifiedGlutenFree: false,
    CertifiedDairyFree: false,
    CertifiedLactoseFree: false
  });

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (query.trim() !== "") params.append("query", query.trim());

    // Add selected ingredients
    let allIngredients = [...selectedIngredients];
    if (useCustomIngredient && customIngredient.trim() !== "") {
      allIngredients.push(customIngredient.trim());
    }
    
    if (allIngredients.length > 0) {
      params.append("ingredients", allIngredients.join(","));
    }

    // Append certifications only if selected
    Object.keys(certifications).forEach((cert) => {
      if (certifications[cert]) {
        params.append(cert, "1");
      }
    });

    navigate(`/items?${params.toString()}`); // Navigate with query params
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center">
      <header className="w-full bg-blue-600 p-4 text-white text-center text-lg font-bold">
        Food Gyde
      </header>
      <main className="flex-grow flex flex-col items-center justify-center px-4">
        {/* Search Input */}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for items or enter a barcode"
          className="w-full max-w-lg p-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Certification Filters */}
        <div className="mt-4 w-full max-w-lg">
          <h3 className="text-gray-700 font-semibold mb-2">Manufacturer Certified</h3>
          <div className="flex flex-wrap gap-4">
            {Object.keys(certifications).map((cert) => {
              const labelMap = {
                CertifiedVegan: "Vegan",
                CertifiedPlantBased: "Plant-Based",
                CertifiedVegetarian: "Vegetarian",
                CertifiedKosher: "Kosher",
                CertifiedGlutenFree: "Gluten-Free",
                CertifiedDairyFree: "Dairy-Free",
                CertifiedLactoseFree: "Lactose-Free"
              };

              return (
                <label key={cert} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={certifications[cert]}
                    onChange={() =>
                      setCertifications({ ...certifications, [cert]: !certifications[cert] })
                    }
                    className="mr-2"
                  />
                  {labelMap[cert] || cert} {/* Uses friendly label */}
                </label>
              );
            })}
          </div>
        </div>

        {/* Ingredients Section */}
        <div className="mt-4 w-full max-w-lg">
          <h3 className="text-gray-700 font-semibold mb-2">Exclude Ingredients</h3>
          <div className="flex flex-wrap gap-4">
            {ingredients.map((ingredient) => (
              <label key={ingredient} className="flex items-center">
                <input
                  type="checkbox"
                  value={ingredient}
                  checked={selectedIngredients.includes(ingredient)}
                  onChange={() => handleCheckboxChange(ingredient)}
                  className="mr-2"
                />
                {ingredient}
              </label>
            ))}
          </div>

          {/* "Other" Ingredient Checkbox & Input */}
          <div className="mt-4 flex items-center gap-2">
            <input
              type="checkbox"
              checked={useCustomIngredient}
              onChange={() => setUseCustomIngredient(!useCustomIngredient)}
              className="mr-2"
            />
            <span>Other:</span>
            {useCustomIngredient && (
              <input
                type="text"
                value={customIngredient}
                onChange={(e) => setCustomIngredient(e.target.value)}
                placeholder="Enter an ingredient..."
                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          </div>
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700"
        >
          Search
        </button>
      </main>
    </div>
  );
}
